"use client";

import { Command } from "cmdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  Search,
  Plus,
  Inbox,
  Circle,
  FolderKanban,
  ArrowRight,
  Trash2,
  CircleDot,
  Bot,
  Loader2,
  Send,
  Sparkles,
  UserRound,
  UsersRound,
} from "lucide-react";
import { useStore } from "@/lib/store";
import { createClient } from "@/lib/supabase/client";
import { STATUSES, PRIORITIES } from "@/lib/constants";
import type { StatusId, PriorityId } from "@/lib/types";
import { StatusIcon } from "./ui/StatusIcon";
import { PriorityIcon } from "./ui/PriorityIcon";
import { Avatar } from "./ui/Avatar";
import { FileText, StickyNote, TrendingUp, Clock } from "lucide-react";
import {
  roleCanWrite,
  usersForWorkspace,
  workspaceRoleForUser,
} from "@/lib/workspaceMembers";
import {
  buildTaskViewUrl,
  createEmptyTaskFilters,
} from "@/lib/taskFilters";
import type { AskPautaCitation, AskPautaScope } from "@/lib/askPauta";
import { Modal } from "@/components/ui/Modal";
import { docIdentifier, matchesDocIdentifier, parseDocIdentifier } from "@/lib/docIdentity";

type SearchHit = {
  entity_type: "issue" | "doc" | "note" | "progress_card" | "session";
  entity_id: string;
  identifier: string | null;
  title: string;
  snippet: string;
  rank: number;
};

type CommandAskRequest = {
  nonce: number;
  scope: AskPautaScope;
  label: string;
};

type RecentRow = {
  entity_type:
    | "task"
    | "doc"
    | "project"
    | "team"
    | "progress_card"
    | "agent_session"
    | "person"
    | "agent";
  entity_id: string;
  last_opened_at: string;
};

type RankedSearchResult = {
  key: string;
  source: "entity" | "note" | "route" | "workspace";
  id: string;
  title: string;
  subtitle: string;
  kind?: RecentRow["entity_type"];
  snippet?: string;
};

// Postgres ts_headline builds the FTS snippet with literal <mark>…</mark>
// highlight tags around matches, but it does NOT html-escape the surrounding
// body text — so a body containing literal <script> would reach innerHTML as
// live HTML (stored XSS). HTML-escape the whole string first, then re-introduce
// ONLY the <mark>/</mark> highlight tags so everything else stays inert text.
function escapeFtsSnippet(snippet: string): string {
  const escaped = snippet
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  return escaped
    .replace(/&lt;mark&gt;/g, "<mark>")
    .replace(/&lt;\/mark&gt;/g, "</mark>");
}

export function CommandMenu({
  onNavigate,
  askRequest = null,
}: {
  onNavigate: (view: string, teamId?: string | null) => void;
  askRequest?: CommandAskRequest | null;
}) {
  const router = useRouter();
  const open = useStore((s) => s.commandOpen);
  const setOpen = useStore((s) => s.setCommandOpen);
  const setCreateOpen = useStore((s) => s.setCreateOpen);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const workspaces = useStore((s) => s.workspaces);
  const allIssues = useStore((s) => s.issues);
  const allProjects = useStore((s) => s.projects);
  const allTeams = useStore((s) => s.teams);
  const allDocs = useStore((s) => s.docs);
  const users = useStore((s) => s.users);
  const workspaceMembers = useStore((s) => s.workspaceMembers);
  const currentUserId = useStore((s) => s.currentUserId);
  const selectedIssueId = useStore((s) => s.selectedIssueId);
  const selectIssue = useStore((s) => s.selectIssue);
  const selectDoc = useStore((s) => s.selectDoc);
  const updateIssue = useStore((s) => s.updateIssue);
  const deleteIssue = useStore((s) => s.deleteIssue);
  const requestAgentsModal = useStore((s) => s.requestAgentsModal);
  const progressCards = useStore((s) => s.progressCards);
  const agentSessions = useStore((s) => s.agentSessions);

  const issues = useMemo(
    () => allIssues.filter((i) => i.workspaceId === activeWorkspaceId),
    [allIssues, activeWorkspaceId]
  );
  const projects = useMemo(
    () => allProjects.filter((p) => p.workspaceId === activeWorkspaceId),
    [allProjects, activeWorkspaceId]
  );
  const teams = useMemo(
    () => allTeams.filter((t) => t.workspaceId === activeWorkspaceId),
    [allTeams, activeWorkspaceId]
  );
  const docs = useMemo(
    () => allDocs.filter((d) => d.workspaceId === activeWorkspaceId),
    [allDocs, activeWorkspaceId]
  );
  const workspaceUsers = useMemo(
    () => usersForWorkspace(users, workspaceMembers, activeWorkspaceId),
    [activeWorkspaceId, users, workspaceMembers]
  );
  const canWrite = roleCanWrite(
    workspaceRoleForUser(workspaceMembers, activeWorkspaceId, currentUserId)
  );

  const [page, setPage] = useState<
    "home" | "ask" | "status" | "priority" | "owner" | "executor"
  >("home");
  const [query, setQuery] = useState("");
  const [ftsResults, setFtsResults] = useState<{ workspaceId: string | null; hits: SearchHit[] }>({ workspaceId: null, hits: [] });
  const ftsHits = ftsResults.workspaceId === activeWorkspaceId ? ftsResults.hits : [];
  const [ftsLoading, setFtsLoading] = useState(false);
  const [recents, setRecents] = useState<RecentRow[]>([]);
  const [askResult, setAskResult] = useState<{
    answer: string;
    citations: AskPautaCitation[];
  } | null>(null);
  const [askLoading, setAskLoading] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const consumedAskRequest = useRef<number | null>(null);
  const commandInputRef = useRef<HTMLInputElement>(null);
  const [activeAskScope, setActiveAskScope] = useState<AskPautaScope>({});
  const [activeAskLabel, setActiveAskLabel] = useState("this workspace");

  useEffect(() => {
    if (open) {
      if (
        askRequest &&
        consumedAskRequest.current !== askRequest.nonce
      ) {
        consumedAskRequest.current = askRequest.nonce;
        setActiveAskScope(askRequest.scope);
        setActiveAskLabel(askRequest.label);
        setPage("ask");
      } else {
        setActiveAskScope({});
        setActiveAskLabel("this workspace");
        setPage("home");
      }
      setQuery("");
      setFtsResults({ workspaceId: activeWorkspaceId, hits: [] });
      setAskResult(null);
      setAskError(null);
    }
  }, [askRequest, open]);

  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    let canceled = false;
    void fetch(`/api/workspaces/${activeWorkspaceId}/recents`, {
      cache: "no-store",
    })
      .then(async (response) =>
        response.ok
          ? ((await response.json()) as { recents?: RecentRow[] })
          : { recents: [] }
      )
      .then((payload) => {
        if (!canceled) setRecents(payload.recents ?? []);
      })
      .catch(() => {
        if (!canceled) setRecents([]);
      });
    return () => {
      canceled = true;
    };
  }, [activeWorkspaceId, open]);

  // Debounced workspace-scoped FTS. Runs only when query has >=2 chars and
  // the palette is open. Clears on query empty.
  useEffect(() => {
    if (!open || page !== "home") return;
    const q = query.trim();
    if (q.length < 2 || !activeWorkspaceId) {
      setFtsResults({ workspaceId: activeWorkspaceId, hits: [] });
      setFtsLoading(false);
      return;
    }
    let cancelled = false;
    setFtsLoading(true);
    const t = setTimeout(async () => {
      const sb = createClient();
      const { data, error } = await sb.rpc("workspace_search", {
        ws_id: activeWorkspaceId,
        q,
        result_limit: 15,
      });
      if (cancelled) return;
      setFtsLoading(false);
      if (error) {
        setFtsResults({ workspaceId: activeWorkspaceId, hits: [] });
        return;
      }
      setFtsResults({ workspaceId: activeWorkspaceId, hits: (data ?? []) as SearchHit[] });
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, open, page, activeWorkspaceId]);

  const selectedIssue = issues.find((i) => i.id === selectedIssueId);

  function openSubmenu(
    nextPage: "status" | "priority" | "owner" | "executor"
  ) {
    setPage(nextPage);
    setQuery("");
  }

  const recentEntities = useMemo(
    () =>
      recents
        .map((recent) => {
          if (recent.entity_type === "task") {
            const entity = issues.find((issue) => issue.id === recent.entity_id);
            return entity
              ? {
                  ...recent,
                  title: `${entity.identifier} · ${entity.title}`,
                  subtitle: "Task",
                }
              : null;
          }
          if (recent.entity_type === "doc") {
            const entity = docs.find((doc) => doc.id === recent.entity_id);
            return entity
              ? { ...recent, title: docIdentifier(entity) ? `${docIdentifier(entity)} · ${entity.title}` : entity.title, subtitle: "Doc" }
              : null;
          }
          if (recent.entity_type === "project") {
            const entity = projects.find(
              (project) => project.id === recent.entity_id
            );
            return entity
              ? { ...recent, title: entity.name, subtitle: "Project" }
              : null;
          }
          if (recent.entity_type === "team") {
            const entity = teams.find((team) => team.id === recent.entity_id);
            return entity
              ? { ...recent, title: entity.name, subtitle: "Team" }
              : null;
          }
          if (
            recent.entity_type === "person" ||
            recent.entity_type === "agent"
          ) {
            const entity = workspaceUsers.find(
              (user) => user.id === recent.entity_id
            );
            return entity
              ? {
                  ...recent,
                  title: entity.name,
                  subtitle: recent.entity_type === "agent" ? "Agent" : "Person",
                }
              : null;
          }
          if (recent.entity_type === "progress_card") {
            const entity = progressCards.find(
              (card) => card.id === recent.entity_id
            );
            return entity
              ? { ...recent, title: entity.title, subtitle: "Progress" }
              : null;
          }
          const entity = agentSessions.find(
            (session) => session.id === recent.entity_id
          );
          return entity
            ? { ...recent, title: entity.name, subtitle: "Agent session" }
            : null;
        })
        .filter(
          (
            entity
          ): entity is RecentRow & { title: string; subtitle: string } =>
            entity != null
        )
        .slice(0, 5),
    [
      agentSessions,
      docs,
      issues,
      progressCards,
      projects,
      recents,
      teams,
      workspaceUsers,
    ]
  );

  const localMatches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length < 1) return [];
    const contains = (...values: Array<string | null | undefined>) =>
      values.some((value) => value?.toLowerCase().includes(term));
    const requestedDocIdentifier = parseDocIdentifier(query);
    return [
      ...issues
        .filter((issue) =>
          contains(issue.identifier, issue.title, issue.description)
        )
        .sort((a, b) => requestedDocIdentifier
          ? Number(b.identifier.toUpperCase() === requestedDocIdentifier)
            - Number(a.identifier.toUpperCase() === requestedDocIdentifier)
          : 0)
        .slice(0, 8)
        .map((entity) => ({
          kind: "task" as const,
          id: entity.id,
          title: `${entity.identifier} · ${entity.title}`,
          subtitle: "Task",
        })),
      ...projects
        .filter((project) => contains(project.name, project.description))
        .slice(0, 6)
        .map((entity) => ({
          kind: "project" as const,
          id: entity.id,
          title: entity.name,
          subtitle: "Project",
        })),
      ...docs
        .filter((doc) => requestedDocIdentifier
          ? matchesDocIdentifier(doc, requestedDocIdentifier)
          : contains(docIdentifier(doc), doc.title))
        .slice(0, 6)
        .map((entity) => ({
          kind: "doc" as const,
          id: entity.id,
          title: docIdentifier(entity) ? `${docIdentifier(entity)} · ${entity.title}` : entity.title,
          subtitle: "Doc",
        })),
      ...teams
        .filter((team) => contains(team.name, team.key, team.description))
        .slice(0, 6)
        .map((entity) => ({
          kind: "team" as const,
          id: entity.id,
          title: entity.name,
          subtitle: "Team",
        })),
      ...workspaceUsers
        .filter((user) => contains(user.name, user.email))
        .slice(0, 8)
        .map((entity) => ({
          kind: entity.isBot ? ("agent" as const) : ("person" as const),
          id: entity.id,
          title: entity.name,
          subtitle: entity.isBot ? "Agent" : "Person",
        })),
    ].slice(0, 18);
  }, [docs, issues, projects, query, teams, workspaceUsers]);

  const rankedSearchResults = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [] as RankedSearchResult[];
    const results: RankedSearchResult[] = [];
    const seen = new Set<string>();
    const requestedDocIdentifier = parseDocIdentifier(query);
    const exactReferences = new Map<string, number>();
    if (requestedDocIdentifier) {
      // A DOC-prefixed issue team can coexist with document references. Both
      // exact identities precede incidental body mentions and the result cap.
      for (const doc of docs) {
        if (matchesDocIdentifier(doc, requestedDocIdentifier)) exactReferences.set(`doc:${doc.id}`, 2);
      }
      for (const issue of issues) {
        if (issue.identifier.toUpperCase() === requestedDocIdentifier) exactReferences.set(`task:${issue.id}`, 1);
      }
      for (const hit of ftsHits) {
        if (hit.identifier?.toUpperCase() !== requestedDocIdentifier) continue;
        if (hit.entity_type === "doc") exactReferences.set(`doc:${hit.entity_id}`, 2);
        else if (hit.entity_type === "issue") exactReferences.set(`task:${hit.entity_id}`, 1);
      }
    }
    const add = (result: RankedSearchResult) => {
      if (seen.has(result.key) || (!requestedDocIdentifier && results.length >= 10)) return;
      seen.add(result.key);
      results.push(result);
    };

    for (const entity of localMatches) {
      add({
        key: `${entity.kind}:${entity.id}`,
        source: "entity",
        id: entity.id,
        title: entity.title,
        subtitle: entity.subtitle,
        kind: entity.kind,
      });
    }

    for (const hit of ftsHits) {
      const kind =
        hit.entity_type === "issue"
          ? "task"
          : hit.entity_type === "session"
            ? "agent_session"
            : hit.entity_type === "progress_card"
              ? "progress_card"
              : hit.entity_type === "doc"
                ? "doc"
                : null;
      add({
        key: `${kind ?? "note"}:${hit.entity_id}`,
        source: kind ? "entity" : "note",
        id: hit.entity_id,
        title: hit.identifier
          ? `${hit.identifier} · ${hit.title}`
          : hit.title,
        subtitle:
          hit.entity_type === "issue"
            ? "Task"
            : hit.entity_type === "progress_card"
              ? "Progress"
              : hit.entity_type === "session"
                ? "Agent session"
                : hit.entity_type === "doc"
                  ? "Doc"
                  : "Note",
        kind: kind ?? undefined,
        // An FTS excerpt can start inside preserved history or machine context.
        // The complete source is needed to project it safely; doc title/ID are
        // sufficient here and the reader opens the authenticated current body.
        snippet: hit.entity_type === "doc" ? undefined : hit.snippet,
      });
    }

    const routes = [
      { id: "inbox", title: "Inbox" },
      { id: "my-issues", title: "My Tasks" },
      { id: "pending", title: "Pendências" },
      { id: "notifications", title: "Notificações" },
      { id: "projects", title: "Projects" },
      { id: "docs", title: "Docs" },
      { id: "progress", title: "Progress" },
      { id: "cycles", title: "Cycles" },
    ];
    for (const route of routes) {
      if (!route.title.toLowerCase().includes(term)) continue;
      add({
        key: `route:${route.id}`,
        source: "route",
        id: route.id,
        title: route.title,
        subtitle: "Go to",
      });
    }

    for (const workspace of workspaces) {
      if (!workspace.name.toLowerCase().includes(term)) continue;
      add({
        key: `workspace:${workspace.id}`,
        source: "workspace",
        id: workspace.slug,
        title: workspace.name,
        subtitle:
          workspace.id === activeWorkspaceId
            ? "Current workspace"
            : "Switch workspace",
      });
    }

    return requestedDocIdentifier
      ? results.sort((a, b) => (exactReferences.get(b.key) ?? 0) - (exactReferences.get(a.key) ?? 0)).slice(0, 10)
      : results;
  }, [activeWorkspaceId, docs, ftsHits, issues, localMatches, query, workspaces]);

  function recordRecent(entityType: RecentRow["entity_type"], entityId: string) {
    if (!activeWorkspaceId || entityId.includes("_")) return;
    void fetch(`/api/workspaces/${activeWorkspaceId}/recents`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        entity_type: entityType,
        entity_id: entityId,
      }),
    }).catch(() => {});
  }

  function openEntity(kind: RecentRow["entity_type"], id: string) {
    recordRecent(kind, id);
    if (kind === "task") selectIssue(id);
    else if (kind === "doc") {
      onNavigate("docs");
      selectDoc(id);
    } else if (kind === "project") onNavigate(`project:${id}`);
    else if (kind === "team") onNavigate(`team:${id}`, id);
    else if (kind === "progress_card") onNavigate("progress");
    else if (kind === "agent_session") onNavigate("agent-runs");
    else if (kind === "agent" && selectedIssue && canWrite) {
      updateIssue(selectedIssue.id, { executorAgentId: id });
    } else if (kind === "agent") {
      requestAgentsModal();
    } else {
      const filters = createEmptyTaskFilters();
      filters.ownerIds = [id];
      window.location.assign(
        buildTaskViewUrl(window.location.href, filters, {
          view: "tasks",
          teamId: null,
          search: "",
          layout: "list",
          stripEntityDeepLinks: true,
        })
      );
    }
    setOpen(false);
  }

  function openSearchResult(result: RankedSearchResult) {
    if (result.source === "entity" && result.kind) {
      openEntity(result.kind, result.id);
      return;
    }
    if (result.source === "note") {
      onNavigate("notes");
      setOpen(false);
      return;
    }
    if (result.source === "route") {
      onNavigate(result.id);
      setOpen(false);
      return;
    }
    if (result.source === "workspace") {
      setOpen(false);
      router.push(`/${result.id}#home`);
    }
  }

  async function askPauta() {
    const question = query.trim();
    if (question.length < 3 || !activeWorkspaceId || askLoading) return;
    setAskLoading(true);
    setAskError(null);
    setAskResult(null);
    try {
      const response = await fetch(
        `/api/workspaces/${activeWorkspaceId}/ask`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            question,
            scope: activeAskScope,
          }),
        }
      );
      const payload = (await response.json().catch(() => ({}))) as {
        answer?: string;
        citations?: AskPautaCitation[];
        error?: string;
      };
      if (!response.ok || !payload.answer) {
        throw new Error(payload.error ?? "ask_failed");
      }
      setAskResult({
        answer: payload.answer,
        citations: payload.citations ?? [],
      });
    } catch (cause) {
      setAskError(
        cause instanceof Error && cause.message === "rate_limited"
          ? "Too many questions at once. Wait a moment and try again."
          : "Pauta could not answer that question. Try again."
      );
    } finally {
      setAskLoading(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      width={620}
      ariaLabel="Global command menu"
    >
      <Command
        loop
        label="Global command menu"
        filter={(value, search) => {
          if (!search) return 1;
          return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
        }}
      >
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--border)]">
                {page !== "home" && (
                  <button
                    type="button"
                    onClick={() => {
                      setPage("home");
                      setQuery("");
                      setAskResult(null);
                      setAskError(null);
                    }}
                    className="focus-ring rounded border border-[var(--border)] px-2 py-0.5 text-[11px] text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)]"
                  >
                    ← Back
                  </button>
                )}
                <Search size={14} className="text-[var(--fg-tertiary)]" />
                <Command.Input
                  ref={commandInputRef}
                  autoFocus
                  aria-label={
                    page === "ask"
                      ? `Ask about ${activeAskLabel}`
                      : "Search or enter a command"
                  }
                  aria-busy={page === "ask" && askLoading}
                  disabled={page === "ask" && askLoading}
                  value={query}
                  onValueChange={setQuery}
                  onKeyDown={(event) => {
                    if (
                      page === "ask" &&
                      event.key === "Enter" &&
                      !event.metaKey &&
                      !event.ctrlKey
                    ) {
                      event.preventDefault();
                      void askPauta();
                    }
                  }}
                  placeholder={
                    page === "ask"
                      ? `Ask about ${activeAskLabel}…`
                      : page === "status"
                      ? "Change status to…"
                      : page === "priority"
                      ? "Change priority to…"
                      : page === "owner"
                      ? "Set human owner…"
                      : page === "executor"
                      ? "Delegate to agent…"
                      : "Type a command or search…"
                  }
                  className="min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-[var(--fg-muted)] disabled:cursor-wait"
                />
                <kbd className="kbd">Esc</kbd>
              </div>

              <Command.List className="max-h-[420px] overflow-y-auto scrollbar-thin p-1.5">
                {page !== "ask" && (
                  <Command.Empty className="px-3 py-6 text-center text-[12.5px] text-[var(--fg-muted)]">
                    No results found.
                  </Command.Empty>
                )}

                {page === "home" && query.trim().length === 0 && (
                  <>
                    <Command.Group heading="Actions" className="cmdk-group">
                      <Row
                        value="ask pauta"
                        icon={<Sparkles size={14} />}
                        onSelect={() => {
                          setActiveAskScope({});
                          setActiveAskLabel("this workspace");
                          setPage("ask");
                          setQuery("");
                          setAskResult(null);
                          setAskError(null);
                        }}
                      >
                        Ask Pauta
                        <span className="ml-auto text-[10.5px] text-[var(--fg-muted)]">
                          Answers with sources
                        </span>
                      </Row>
                      {canWrite && (
                        <Row
                          value="create new task"
                          icon={<Plus size={14} />}
                          onSelect={() => {
                            setOpen(false);
                            setCreateOpen(true);
                          }}
                        >
                          Create task
                        </Row>
                      )}
                    </Command.Group>

                    {recentEntities.length > 0 && (
                      <Command.Group heading="Recent" className="cmdk-group">
                        {recentEntities.map((entity) => (
                          <Row
                            key={`${entity.entity_type}:${entity.entity_id}`}
                            value={`recent ${entity.title} ${entity.subtitle}`}
                            icon={<PaletteEntityIcon kind={entity.entity_type} />}
                            onSelect={() =>
                              openEntity(entity.entity_type, entity.entity_id)
                            }
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {entity.title}
                            </span>
                            <span className="text-[10px] text-[var(--fg-muted)]">
                              {entity.subtitle}
                            </span>
                          </Row>
                        ))}
                      </Command.Group>
                    )}

                    <Command.Group heading="Suggested" className="cmdk-group">
                      <Row value="pendências pending" icon={<Inbox size={14} />}
                        onSelect={() => { onNavigate("pending"); setOpen(false); }}>
                        Revisar pendências
                      </Row>
                      <Row value="notificações notifications" icon={<Circle size={14} />}
                        onSelect={() => { onNavigate("notifications"); setOpen(false); }}>
                        Abrir notificações
                      </Row>
                      <Row
                        value="inbox"
                        icon={<Inbox size={14} />}
                        onSelect={() => {
                          onNavigate("inbox");
                          setOpen(false);
                        }}
                      >
                        Review Inbox
                      </Row>
                      <Row
                        value="my tasks"
                        icon={<Circle size={14} />}
                        onSelect={() => {
                          onNavigate("my-issues");
                          setOpen(false);
                        }}
                      >
                        Open My Tasks
                      </Row>
                      <Row
                        value="projects"
                        icon={<FolderKanban size={14} />}
                        onSelect={() => {
                          onNavigate("projects");
                          setOpen(false);
                        }}
                      >
                        Browse Projects
                      </Row>
                    </Command.Group>
                  </>
                )}

                {page === "home" && query.trim().length > 0 && (
                  <>
                    <Command.Group heading="Actions" className="cmdk-group">
                      <Row
                        value={`ask pauta ${query}`}
                        icon={<Sparkles size={14} />}
                        onSelect={() => {
                          setActiveAskScope({});
                          setActiveAskLabel("this workspace");
                          setPage("ask");
                          setAskResult(null);
                          setAskError(null);
                        }}
                      >
                        Ask Pauta “{query.slice(0, 64)}{query.length > 64 ? "…" : ""}”
                      </Row>
                      {canWrite && (
                        <Row
                          value="create new task"
                          icon={<Plus size={14} />}
                          onSelect={() => {
                            setOpen(false);
                            setCreateOpen(true);
                          }}
                        >
                          Create new task
                        </Row>
                      )}
                      {canWrite && selectedIssue && (
                        <>
                          <Row
                            value="change status"
                            icon={<CircleDot size={14} />}
                            onSelect={() => openSubmenu("status")}
                          >
                            Change status…
                          </Row>
                          <Row
                            value="change priority"
                            icon={<CircleDot size={14} />}
                            onSelect={() => openSubmenu("priority")}
                          >
                            Change priority…
                          </Row>
                          <Row
                            value="set owner"
                            icon={<UserRound size={14} />}
                            onSelect={() => openSubmenu("owner")}
                          >
                            Set human owner…
                          </Row>
                          <Row
                            value="delegate agent"
                            icon={<Bot size={14} />}
                            onSelect={() => openSubmenu("executor")}
                          >
                            Delegate to agent…
                          </Row>
                          <Row
                            value="delete task"
                            icon={<Trash2 size={14} />}
                            onSelect={() => {
                              if (confirm("Delete this task?")) {
                                deleteIssue(selectedIssue.id);
                                setOpen(false);
                              }
                            }}
                          >
                            Delete task
                          </Row>
                        </>
                      )}
                    </Command.Group>

                    {rankedSearchResults.length > 0 && (
                      <Command.Group
                        heading={ftsLoading ? "Results (searching…)" : "Results"}
                        className="cmdk-group"
                      >
                        {rankedSearchResults.map((result) => (
                          <Row
                            key={result.key}
                            value={`${result.title} ${result.subtitle} ${result.snippet ?? ""}`}
                            icon={
                              result.kind ? (
                                <PaletteEntityIcon kind={result.kind} />
                              ) : result.source === "note" ? (
                                <StickyNote size={13} />
                              ) : result.source === "workspace" ? (
                                <FolderKanban size={13} />
                              ) : (
                                <ArrowRight size={13} />
                              )
                            }
                            onSelect={() => openSearchResult(result)}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="truncate">{result.title}</div>
                              {result.snippet && (
                                <div
                                  className="truncate text-[10.5px] text-[var(--fg-muted)] [&_mark]:rounded [&_mark]:bg-[var(--accent-bg)] [&_mark]:px-0.5 [&_mark]:text-[var(--accent)]"
                                  dangerouslySetInnerHTML={{
                                    __html: escapeFtsSnippet(result.snippet),
                                  }}
                                />
                              )}
                            </div>
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-[var(--fg-muted)]">
                              {result.subtitle}
                            </span>
                            <ArrowRight
                              size={12}
                              className="ml-1 text-[var(--fg-muted)]"
                            />
                          </Row>
                        ))}
                      </Command.Group>
                    )}
                  </>
                )}

                {page === "ask" && (
                  <div className="p-3">
                    <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)] p-3">
                      <div className="flex items-center gap-2 text-[11.5px] font-medium text-[var(--fg-secondary)]">
                        <Sparkles size={13} className="text-[var(--accent)]" />
                        Ask about {activeAskLabel}
                      </div>
                      {!askResult && !askLoading && !askError && (
                        <p className="mt-2 text-[11.5px] leading-5 text-[var(--fg-tertiary)]">
                          Ask what is blocked, what changed, where a decision
                          lives, or what should happen next. Pauta searches
                          tasks, docs, projects, progress, and agent runs.
                        </p>
                      )}
                      <div aria-live="polite" aria-atomic="true">
                        {askLoading && (
                          <div
                            role="status"
                            className="mt-4 flex items-center gap-2 py-3 text-[11.5px] text-[var(--fg-tertiary)]"
                          >
                            <Loader2 size={13} className="animate-spin" />
                            Reading the most relevant workspace context…
                          </div>
                        )}
                      {askError && (
                        <p
                          role="alert"
                          className="mt-3 rounded-md border border-[var(--red)]/25 bg-[var(--red)]/10 px-3 py-2 text-[11.5px] text-[var(--red)]"
                        >
                          {askError}
                        </p>
                      )}
                      {askResult && (
                        <div className="mt-3">
                          <p className="whitespace-pre-wrap text-[12.5px] leading-5 text-[var(--fg-secondary)]">
                            {askResult.answer}
                          </p>
                          {askResult.citations.length > 0 && (
                            <div className="mt-4 border-t border-[var(--border)] pt-3">
                              <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">
                                Sources
                              </div>
                              <div className="space-y-1">
                                {askResult.citations.map((citation) => (
                                  <a
                                    key={`${citation.kind}:${citation.id}`}
                                    href={citation.url}
                                    onClick={() => {
                                      const recentKind =
                                        citation.kind === "task"
                                          ? "task"
                                          : citation.kind === "agent_run"
                                            ? null
                                            : citation.kind;
                                      if (recentKind) {
                                        recordRecent(
                                          recentKind as RecentRow["entity_type"],
                                          citation.id
                                        );
                                      }
                                      setOpen(false);
                                    }}
                                    className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-[11.5px] text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
                                  >
                                    <span className="font-mono text-[10px] text-[var(--accent)]">
                                      {citation.reference}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate">
                                      {citation.title}
                                    </span>
                                    <ArrowRight
                                      size={11}
                                      className="text-[var(--fg-muted)]"
                                    />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (askResult) {
                          setAskResult(null);
                          setAskError(null);
                          setQuery("");
                          requestAnimationFrame(() =>
                            commandInputRef.current?.focus()
                          );
                          return;
                        }
                        void askPauta();
                      }}
                      disabled={
                        askLoading || (!askResult && query.trim().length < 3)
                      }
                      className="mt-2 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md bg-[var(--accent)] px-3 text-[11.5px] font-medium text-white transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-40"
                    >
                      {askLoading ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Send size={12} />
                      )}
                      {askResult ? "Ask another question" : "Ask Pauta"}
                    </button>
                  </div>
                )}

                {page === "status" && canWrite && selectedIssue && (
                  <Command.Group heading="Status" className="cmdk-group">
                    {STATUSES.map((s) => (
                      <Row
                        key={s.id}
                        value={s.name}
                        icon={<StatusIcon status={s.id} size={13} />}
                        onSelect={() => {
                          updateIssue(selectedIssue.id, { status: s.id as StatusId });
                          setOpen(false);
                        }}
                      >
                        {s.name}
                      </Row>
                    ))}
                  </Command.Group>
                )}

                {page === "priority" && canWrite && selectedIssue && (
                  <Command.Group heading="Priority" className="cmdk-group">
                    {PRIORITIES.map((p) => (
                      <Row
                        key={p.id}
                        value={p.name}
                        icon={<PriorityIcon priority={p.id} size={13} />}
                        onSelect={() => {
                          updateIssue(selectedIssue.id, { priority: p.id as PriorityId });
                          setOpen(false);
                        }}
                      >
                        {p.name}
                      </Row>
                    ))}
                  </Command.Group>
                )}

                {page === "owner" && canWrite && selectedIssue && (
                  <Command.Group heading="People" className="cmdk-group">
                    <Row
                      value="no owner"
                      icon={<Avatar user={null} size={16} />}
                      onSelect={() => {
                        updateIssue(selectedIssue.id, { ownerId: null });
                        setOpen(false);
                      }}
                    >
                      No human owner
                    </Row>
                    {workspaceUsers.filter((user) => !user.isBot).map((u) => (
                      <Row
                        key={u.id}
                        value={u.name}
                        icon={<Avatar user={u} size={16} />}
                        onSelect={() => {
                          updateIssue(selectedIssue.id, { ownerId: u.id });
                          setOpen(false);
                        }}
                      >
                        {u.name}
                      </Row>
                    ))}
                  </Command.Group>
                )}

                {page === "executor" && canWrite && selectedIssue && (
                  <Command.Group heading="Agents" className="cmdk-group">
                    <Row
                      value="no agent"
                      icon={<Avatar user={null} size={16} />}
                      onSelect={() => {
                        updateIssue(selectedIssue.id, {
                          executorAgentId: null,
                        });
                        setOpen(false);
                      }}
                    >
                      No agent
                    </Row>
                    {workspaceUsers.filter((user) => user.isBot).map((u) => (
                      <Row
                        key={u.id}
                        value={u.name}
                        icon={<Avatar user={u} size={16} />}
                        onSelect={() => {
                          updateIssue(selectedIssue.id, {
                            executorAgentId: u.id,
                          });
                          setOpen(false);
                        }}
                      >
                        {u.name}
                      </Row>
                    ))}
                  </Command.Group>
                )}
              </Command.List>

              <div className="flex items-center justify-between border-t border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-2 text-[11px] text-[var(--fg-muted)]">
                {page === "ask" ? (
                  <>
                    <div className="flex items-center gap-2">
                      <kbd className="kbd">↵</kbd>
                      to ask
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="kbd">Esc</kbd>
                      to close
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <kbd className="kbd">↑</kbd>
                      <kbd className="kbd">↓</kbd>
                      to navigate
                    </div>
                    <div className="flex items-center gap-2">
                      <kbd className="kbd">↵</kbd>
                      to select
                    </div>
                  </>
                )}
              </div>
      </Command>
    </Modal>
  );
}

function Row({
  value,
  icon,
  children,
  onSelect,
}: {
  value: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12.5px] text-[var(--fg-secondary)] cursor-pointer data-[selected=true]:bg-[var(--bg-hover)] data-[selected=true]:text-[var(--fg)]"
    >
      {icon && <span className="text-[var(--fg-tertiary)]">{icon}</span>}
      {children}
    </Command.Item>
  );
}

function PaletteEntityIcon({ kind }: { kind: RecentRow["entity_type"] }) {
  const size = 13;
  if (kind === "task") return <CircleDot size={size} />;
  if (kind === "doc") return <FileText size={size} />;
  if (kind === "project") return <FolderKanban size={size} />;
  if (kind === "team") return <UsersRound size={size} />;
  if (kind === "person") return <UserRound size={size} />;
  if (kind === "agent") return <Bot size={size} />;
  if (kind === "progress_card") return <TrendingUp size={size} />;
  return <Clock size={size} />;
}
